import { Injectable } from '@angular/core';
import { InMemoryHttpClient } from './in-memory-http-client';
import { Observable } from 'rxjs';
import { RequestInfo, ResponseOptions, STATUS } from 'angular-in-memory-web-api';
import moment from 'moment';
import { AvailableEmployeeResponse, CapabilityViewResponse } from '../../../interfaces/response/EmployeeResponse';
import { EmployeePositionId } from '../../../classes/models/employee/Employee';

/**
 * This service intercepts {@link CapabilityService} requests and redirects them to an in-memory data store.
 */
@Injectable({
  providedIn: 'root'
})
export class InMemoryCapabilityDataService implements InMemoryHttpClient {

  /**
   * Intercepts HTTP DELETE request of {@link CapabilityService}.
   * @param reqInfo information about the current HTTP DELETE request.
   */
  delete(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return undefined;
  }

  /**
   * Intercepts HTTP GET request of {@link CapabilityService}. Gets all required records from an in-memory data store.
   * @param reqInfo information about the current HTTP GET request.
   */
  get(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
        return {
          body: this.getCapabilityViews(),
          status: STATUS.OK
        };
      }
    );
  }

  /**
   * Intercepts HTTP POST request of {@link CapabilityService}.
   * @param reqInfo information about the current HTTP POST request.
   */
  post(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return undefined;
  }

  /**
   * Intercepts HTTP PUT request of {@link CapabilityService}.
   * @param reqInfo information about the current HTTP PUT request.
   */
  put(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return undefined;
  }

  /**
   * Intercepts HTTP PATCH request of {@link CapabilityService}.
   * @param reqInfo information about the current HTTP PATCH request.
   */
  patch(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return undefined;
  }

  private getCapabilityViews(): CapabilityViewResponse[] {
    const monday = moment().startOf('week').isoWeekday(1).format('YYYY-MM-DD');
    const tuesday = moment().startOf('week').isoWeekday(2).format('YYYY-MM-DD');
    const wednesday = moment().startOf('week').isoWeekday(3).format('YYYY-MM-DD');
    const thursday = moment().startOf('week').isoWeekday(4).format('YYYY-MM-DD');
    const friday = moment().startOf('week').isoWeekday(5).format('YYYY-MM-DD');
    const saturday = moment().startOf('week').isoWeekday(6).format('YYYY-MM-DD');
    const sunday = moment().startOf('week').isoWeekday(7).format('YYYY-MM-DD');
    const EP_ID_1 = new EmployeePositionId('AAA', 'DFSAAA');
    const EP_ID_2 = new EmployeePositionId('BBB', 'DFSBBB');
    const EP_ID_3 = new EmployeePositionId('CCC', 'DFSCCC');
    const EP_ID_4 = new EmployeePositionId('DDD', 'DFSDDD');
    const EP_ID_5 = new EmployeePositionId('EEE', 'DFSEEE');
    const EP_ID_6 = new EmployeePositionId('FFF', 'DFSFFF');
    return [{
      requiredCapability: 'KEY_HOLDER', date: monday, startTime: '07:45:00', endTime: '08:00:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_1, EP_ID_2, EP_ID_3]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: monday, startTime: '19:45:00', endTime: '20:00:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_2, EP_ID_3, EP_ID_5]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: tuesday, startTime: '08:45:00', endTime: '09:00:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_2, EP_ID_3, EP_ID_4, EP_ID_6]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: tuesday, startTime: '21:00:00', endTime: '21:15:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_3, EP_ID_5]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: wednesday, startTime: '08:45:00', endTime: '09:00:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_2, EP_ID_3, EP_ID_6]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: wednesday, startTime: '21:00:00', endTime: '21:15:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_3, EP_ID_5]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: thursday, startTime: '08:45:00', endTime: '09:00:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_2, EP_ID_4]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: thursday, startTime: '21:00:00', endTime: '21:15:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_1, EP_ID_2, EP_ID_3, EP_ID_6]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: friday, startTime: '08:45:00', endTime: '09:00:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_2, EP_ID_3]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: friday, startTime: '21:00:00', endTime: '21:15:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_2, EP_ID_5]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: saturday, startTime: '08:45:00', endTime: '09:00:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_2, EP_ID_4]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: saturday, startTime: '21:00:00', endTime: '21:15:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_1, EP_ID_2, EP_ID_6]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: sunday, startTime: '08:45:00', endTime: '09:00:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_2, EP_ID_4]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'KEY_HOLDER', date: sunday, startTime: '21:00:00', endTime: '21:15:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_1, EP_ID_3, EP_ID_6]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'BANKING', date: monday, startTime: '00:00:00', endTime: '23:59:59',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_3, EP_ID_6]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'BANKING', date: tuesday, startTime: '10:00:00', endTime: '10:30:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_3, EP_ID_4, EP_ID_5]),
      requiredEmployees: 1
    }, {
      requiredCapability: 'FURNITURE_CHANGEOVER', date: wednesday, startTime: '09:00:00', endTime: '10:00:00',
      availableEmployees: this.toAvailableEmployeeResponses([EP_ID_1, EP_ID_2, EP_ID_3]),
      requiredEmployees: 2
    }];
  }

  private toAvailableEmployeeResponses(employeePositionIds: EmployeePositionId[]): AvailableEmployeeResponse[] {
    return employeePositionIds.map(id => this.toAvailableEmployeeResponse(id));
  }

  private toAvailableEmployeeResponse(employeePositionId: EmployeePositionId): AvailableEmployeeResponse {
    return {
      itrentEmployeeId: employeePositionId.parseEmployeeId(),
      itrentPositionId: employeePositionId.parsePositionId()
    };
  }
}
